/**
 * FluxNodeActiveAlerts Component
 *
 * Displays active (unresolved) alerts and offline nodes.
 * Allows users to acknowledge, dismiss, or remove nodes from monitoring.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { useFluxNodeActiveAlerts, FluxNodeAlert, OfflineNode, ResolutionType } from '../../hooks/useFluxNodeActiveAlerts';

interface AlertCardProps {
  alert: FluxNodeAlert;
  onResolve: (type: ResolutionType) => void;
  isProcessing: boolean;
}

function AlertCard({ alert, onResolve, isProcessing }: AlertCardProps) {
  const { t } = useTranslation();
  const [showActions, setShowActions] = useState(false);

  const getAlertIcon = () => {
    switch (alert.alert_type) {
      case 'node_offline':
        return <Icons.AlertTriangle size={20} style={{ color: '#ef4444' }} />;
      case 'node_new':
        return <Icons.Plus size={20} style={{ color: '#22c55e' }} />;
      case 'rank_change':
        return <Icons.TrendingUp size={20} style={{ color: '#f59e0b' }} />;
      case 'tier_change':
        return <Icons.Layers size={20} style={{ color: '#8b5cf6' }} />;
      default:
        return <Icons.Bell size={20} style={{ color: 'var(--primary)' }} />;
    }
  };

  const getAlertColor = () => {
    switch (alert.alert_type) {
      case 'node_offline':
        return '#ef4444';
      case 'node_new':
        return '#22c55e';
      case 'rank_change':
        return '#f59e0b';
      default:
        return '#8b5cf6';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const details = alert.details as Record<string, unknown> | null;
  const tier = details?.previousSnapshot
    ? (details.previousSnapshot as Record<string, unknown>).tier as string
    : null;
  const ip = details?.previousSnapshot
    ? (details.previousSnapshot as Record<string, unknown>).ip_address as string
    : null;

  return (
    <div
      style={{
        background: 'var(--card)',
        borderRadius: '12px',
        border: '1px solid var(--border)',
        borderLeft: `4px solid ${getAlertColor()}`,
        padding: '16px',
        marginBottom: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1 }}>
          <div style={{ marginTop: '2px' }}>{getAlertIcon()}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--foreground)', marginBottom: '4px' }}>
              {alert.title}
            </div>
            {alert.message && (
              <div style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '8px' }}>
                {alert.message}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '11px' }}>
              {tier && (
                <span style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  color: '#8b5cf6',
                  padding: '2px 8px',
                  borderRadius: '4px',
                }}>
                  {tier}
                </span>
              )}
              {ip && (
                <span style={{
                  background: 'var(--border)',
                  color: 'var(--muted)',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                }}>
                  {ip}
                </span>
              )}
              <span style={{ color: 'var(--muted)' }}>
                {formatDate(alert.created_at)}
              </span>
            </div>
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowActions(!showActions)}
            disabled={isProcessing}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--muted)',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              padding: '4px',
              opacity: isProcessing ? 0.5 : 1,
            }}
          >
            <Icons.MoreVertical size={18} />
          </button>

          {showActions && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '100%',
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                zIndex: 10,
                minWidth: '180px',
                overflow: 'hidden',
              }}
            >
              <button
                onClick={() => { onResolve('acknowledged'); setShowActions(false); }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 14px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--foreground)',
                  fontSize: '13px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--border)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <Icons.Check size={16} style={{ color: '#22c55e' }} />
                {t('fluxNode.activeAlerts.acknowledge', 'Marquer comme vu')}
              </button>
              <button
                onClick={() => { onResolve('dismissed'); setShowActions(false); }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 14px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--foreground)',
                  fontSize: '13px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--border)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <Icons.X size={16} style={{ color: 'var(--muted)' }} />
                {t('fluxNode.activeAlerts.dismiss', 'Ignorer')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface OfflineNodeCardProps {
  node: OfflineNode;
  alerts: FluxNodeAlert[];
  onResolveAlerts: () => void;
  onRemoveNode: () => void;
  isProcessing: boolean;
}

function OfflineNodeCard({ node, alerts, onResolveAlerts, onRemoveNode, isProcessing }: OfflineNodeCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div
      style={{
        background: 'var(--card)',
        borderRadius: '12px',
        border: '1px solid var(--border)',
        borderLeft: '4px solid #ef4444',
        overflow: 'hidden',
        marginBottom: '12px',
      }}
    >
      {/* Node Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px',
          cursor: 'pointer',
          background: expanded ? 'rgba(239, 68, 68, 0.05)' : 'transparent',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Icons.Server size={24} style={{ color: '#ef4444' }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--foreground)' }}>
              {node.tier} Node - {t('fluxNode.activeAlerts.offline', 'Hors ligne')}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', fontFamily: 'monospace' }}>
              {node.ip_address || node.node_key.substring(0, 20) + '...'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {alerts.length > 0 && (
            <span style={{
              background: '#ef4444',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: 600,
            }}>
              {alerts.length} {t('fluxNode.activeAlerts.alertsCount', 'alerte(s)')}
            </span>
          )}
          <Icons.ChevronDown
            size={18}
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.2s ease',
              color: 'var(--muted)',
            }}
          />
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
          {/* Node Details */}
          <div style={{ padding: '12px 0', fontSize: '13px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              <div>
                <span style={{ color: 'var(--muted)' }}>{t('fluxNode.activeAlerts.wallet', 'Wallet')}:</span>
                <div style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--foreground)' }}>
                  {node.wallet_address.substring(0, 12)}...{node.wallet_address.slice(-8)}
                </div>
              </div>
              <div>
                <span style={{ color: 'var(--muted)' }}>{t('fluxNode.activeAlerts.lastSeen', 'Vu pour la derniere fois')}:</span>
                <div style={{ color: 'var(--foreground)' }}>
                  {formatDate(node.last_seen_at)}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{
            display: 'flex',
            gap: '8px',
            paddingTop: '12px',
            borderTop: '1px solid var(--border)',
          }}>
            {alerts.length > 0 && (
              <button
                onClick={onResolveAlerts}
                disabled={isProcessing}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '8px',
                  color: '#22c55e',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  opacity: isProcessing ? 0.5 : 1,
                }}
              >
                <Icons.Check size={16} />
                {t('fluxNode.activeAlerts.acknowledgeAlerts', 'Marquer comme resolu')}
              </button>
            )}

            {!confirmRemove ? (
              <button
                onClick={() => setConfirmRemove(true)}
                disabled={isProcessing}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  color: '#ef4444',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  opacity: isProcessing ? 0.5 : 1,
                }}
              >
                <Icons.Trash2 size={16} />
                {t('fluxNode.activeAlerts.removeFromMonitoring', 'Retirer du monitoring')}
              </button>
            ) : (
              <div style={{ flex: 1, display: 'flex', gap: '8px' }}>
                <button
                  onClick={onRemoveNode}
                  disabled={isProcessing}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '10px 16px',
                    background: '#ef4444',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.5 : 1,
                  }}
                >
                  <Icons.Trash2 size={16} />
                  {t('common.confirm', 'Confirmer')}
                </button>
                <button
                  onClick={() => setConfirmRemove(false)}
                  disabled={isProcessing}
                  style={{
                    padding: '10px 16px',
                    background: 'var(--border)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'var(--foreground)',
                    fontSize: '13px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                  }}
                >
                  {t('common.cancel', 'Annuler')}
                </button>
              </div>
            )}
          </div>

          {/* Info text */}
          <p style={{
            fontSize: '11px',
            color: 'var(--muted)',
            marginTop: '12px',
            padding: '8px',
            background: 'var(--border)',
            borderRadius: '6px',
          }}>
            {t('fluxNode.activeAlerts.removeInfo', 'Retirer le node du monitoring supprimera toutes les alertes associees et arretera la surveillance de ce node. Utile si vous avez migre le node vers une nouvelle IP.')}
          </p>
        </div>
      )}
    </div>
  );
}

interface FluxNodeActiveAlertsProps {
  onClose?: () => void;
}

export function FluxNodeActiveAlerts({ onClose }: FluxNodeActiveAlertsProps) {
  const { t } = useTranslation();
  const {
    activeAlerts,
    offlineNodes,
    alertsByNode,
    isLoading,
    isProcessing,
    isAuthenticated,
    hasActiveAlerts,
    hasOfflineNodes,
    resolveAlerts,
    removeNode,
    resolveAllForNode,
    dismissAllAlerts,
    refetch,
  } = useFluxNodeActiveAlerts();

  if (!isAuthenticated) {
    return (
      <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
        <Icons.Lock size={48} style={{ color: 'var(--muted)', marginBottom: '16px' }} />
        <h3 style={{ marginBottom: '8px' }}>{t('fluxNode.alerts.loginRequired', 'Connexion requise')}</h3>
        <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
          {t('fluxNode.alerts.loginDescription', 'Connectez-vous pour voir vos alertes.')}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
        <Icons.Loader size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
        <p style={{ color: 'var(--muted)' }}>{t('common.loading', 'Chargement...')}</p>
      </div>
    );
  }

  const hasAnyIssues = hasActiveAlerts || hasOfflineNodes;

  return (
    <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid var(--border)',
        background: hasAnyIssues
          ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(249, 115, 22, 0.1))'
          : 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(16, 185, 129, 0.1))',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {hasAnyIssues ? (
            <Icons.AlertTriangle size={24} style={{ color: '#ef4444' }} />
          ) : (
            <Icons.CheckCircle size={24} style={{ color: '#22c55e' }} />
          )}
          <div>
            <h3 style={{ margin: 0, fontSize: '16px' }}>
              {t('fluxNode.activeAlerts.title', 'Alertes Actives')}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
              {hasAnyIssues
                ? t('fluxNode.activeAlerts.issuesDetected', '{{count}} probleme(s) detecte(s)', { count: activeAlerts.length + offlineNodes.length })
                : t('fluxNode.activeAlerts.noIssues', 'Aucun probleme detecte')}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={refetch}
            disabled={isProcessing}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--muted)',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              padding: '4px',
              opacity: isProcessing ? 0.5 : 1,
            }}
            title={t('common.refresh', 'Rafraichir')}
          >
            <Icons.RefreshCw size={18} style={isProcessing ? { animation: 'spin 1s linear infinite' } : {}} />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--muted)',
                cursor: 'pointer',
                padding: '4px',
              }}
            >
              <Icons.X size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '16px 20px', maxHeight: '60vh', overflowY: 'auto' }}>
        {!hasAnyIssues ? (
          <div style={{ textAlign: 'center', padding: '32px 16px' }}>
            <Icons.CheckCircle size={48} style={{ color: '#22c55e', marginBottom: '16px' }} />
            <h4 style={{ margin: '0 0 8px', color: 'var(--foreground)' }}>
              {t('fluxNode.activeAlerts.allClear', 'Tout est en ordre !')}
            </h4>
            <p style={{ color: 'var(--muted)', fontSize: '14px', margin: 0 }}>
              {t('fluxNode.activeAlerts.allClearDesc', 'Tous vos nodes fonctionnent correctement.')}
            </p>
          </div>
        ) : (
          <>
            {/* Offline Nodes Section */}
            {hasOfflineNodes && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  color: '#ef4444',
                  marginBottom: '12px',
                }}>
                  <Icons.Server size={18} />
                  {t('fluxNode.activeAlerts.offlineNodes', 'Nodes Hors Ligne')}
                  <span style={{
                    background: '#ef4444',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '11px',
                  }}>
                    {offlineNodes.length}
                  </span>
                </h4>

                {offlineNodes.map(node => (
                  <OfflineNodeCard
                    key={node.node_key}
                    node={node}
                    alerts={alertsByNode[node.node_key] || []}
                    onResolveAlerts={() => resolveAllForNode(node.node_key, 'acknowledged')}
                    onRemoveNode={() => removeNode(node.node_key, 'Migrated to new IP')}
                    isProcessing={isProcessing}
                  />
                ))}
              </div>
            )}

            {/* Other Alerts Section (not linked to offline nodes) */}
            {Object.entries(alertsByNode).filter(([key]) =>
              !offlineNodes.some(n => n.node_key === key)
            ).map(([nodeKey, alerts]) => (
              alerts.length > 0 && (
                <div key={nodeKey} style={{ marginBottom: '16px' }}>
                  {alerts.map(alert => (
                    <AlertCard
                      key={alert.id}
                      alert={alert}
                      onResolve={(type) => resolveAlerts([alert.id], type)}
                      isProcessing={isProcessing}
                    />
                  ))}
                </div>
              )
            ))}

            {/* Dismiss All Button */}
            {hasActiveAlerts && (
              <div style={{ textAlign: 'center', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
                <button
                  onClick={dismissAllAlerts}
                  disabled={isProcessing}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 20px',
                    background: 'var(--border)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'var(--muted)',
                    fontSize: '13px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.5 : 1,
                  }}
                >
                  <Icons.CheckCircle size={16} />
                  {t('fluxNode.activeAlerts.dismissAll', 'Tout marquer comme lu')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
